import React, { createContext, useContext, useEffect, useMemo, ReactNode } from 'react';
import { Task } from '../../types';
import { useOrganization } from '../OrganizationContext';
import { useAuth } from '../AuthContext';
import { useTaskActions } from './useTaskActions';
import { useTaskDueNotifications } from './useTaskDueNotifications';

interface TasksContextType {
    tasks: Task[];
    isLoading: boolean;
    addTask: (task: Omit<Task, 'id'>) => Promise<void>;
    createTask: (task: Omit<Task, 'id'>) => Promise<void>;
    updateTask: (updatedTask: Task) => Promise<void>;
    toggleTaskCompletion: (taskId: string) => Promise<void>;
    deleteTask: (taskId: string) => Promise<void>;
    refreshTasks: () => Promise<void>;
}

const TasksContext = createContext<TasksContextType | undefined>(undefined);

export const TasksProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { currentOrganization } = useOrganization();
    const { user } = useAuth();

    const {
        tasks, isLoading, setIsLoading,
        addTask, updateTask, toggleTaskCompletion,
        deleteTask, refreshTasks
    } = useTaskActions({ organizationId: currentOrganization?.id });

    useTaskDueNotifications({
        tasks,
        userRole: currentOrganization?.role,
        userId: user?.id
    });

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            if (currentOrganization) await refreshTasks();
            setIsLoading(false);
        };
        loadData();
    }, [currentOrganization, refreshTasks, setIsLoading]);

    const value = useMemo(() => ({
        tasks, isLoading,
        addTask, createTask: addTask,
        updateTask, toggleTaskCompletion,
        deleteTask, refreshTasks
    }), [tasks, isLoading, addTask, updateTask, toggleTaskCompletion, deleteTask, refreshTasks]);

    return (
        <TasksContext.Provider value={value}>
            {children}
        </TasksContext.Provider>
    );
};

export const useTasks = (): TasksContextType => {
    const context = useContext(TasksContext);
    if (!context) {
        throw new Error('useTasks must be used within a TasksProvider');
    }
    return context;
};
